import { useCallback, useMemo } from 'react';

import { useAppSelector } from '../app/hooks';
import { useGetUserSectionsQuery } from '../services/authApi';
import { protectedRoutes } from '../utils/protectedRoutes';
import { ADMIN_ID } from '../utils/utils';

// Sections any authenticated user can reach regardless of their groups.
const ALWAYS_ACCESSIBLE_SECTIONS = ['settings/security'];

/**
 * Single source of truth for "what can this user open".
 *
 * Access is driven by the sections reachable through the user's groups
 * (GET /core/auth/user/sections). Admins (superuser or Admin group) reach every
 * section, so the request is skipped for them. Shared by the router and the
 * side navbar so routes and menu stay in sync.
 */
export function useSectionAccess() {
  const { token } = useAppSelector((state) => state.auth);
  const { groups, isSuperuser } = useAppSelector((state) => state.user.user);

  // Mirrors the backend `is_admin` predicate (superuser or Admin group member).
  const isAdmin =
    Boolean(isSuperuser) ||
    Boolean(groups?.find((group) => group.id === ADMIN_ID));

  const { data: userSections, isLoading: isLoadingSections } =
    useGetUserSectionsQuery(null, { skip: !token || isAdmin });

  const allowedKeys = useMemo(
    () =>
      new Set<string>([
        ...ALWAYS_ACCESSIBLE_SECTIONS,
        ...(userSections || []).map((section) => section.key),
      ]),
    [userSections]
  );

  const canAccessSection = useCallback(
    (key: string) => isAdmin || allowedKeys.has(key),
    [isAdmin, allowedKeys]
  );

  // Resolves a route path to its section via the protectedRoutes map (so shared
  // sections like the vehicle operator area work). Paths with no section fall
  // back to the path itself, which is covered by ALWAYS_ACCESSIBLE_SECTIONS.
  const canAccessPath = useCallback(
    (path: string) => {
      const route = protectedRoutes.find((item) => item.path === path);
      const key = route ? route.key : path.replace(/^\//, '');
      return canAccessSection(key);
    },
    [canAccessSection]
  );

  return { isAdmin, canAccessSection, canAccessPath, isLoadingSections };
}
